import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomTypeahead from '../../../components/CustomTypeahead';
import { Section, Select, controlClass } from '../../../components/EntityList';
import { useLabels } from '../../../hooks/useLabels';
import { toast } from '../../../toasts';
import { getError } from '../../../utils';
import AssetService from '../../../services/asset.service';
import AccountService from '../../../services/account.service';
import BankAccountService from '../../../services/bankAccount.service';
import { pickKey } from './autoMap';

// Pieces of the review step that let the user complete "incomplete" rows:
// pick the record a file value refers to, or type a missing value.

const LOADERS = {
	asset: async () => {
		const { data } = await AssetService.list({ slim: true });
		return data.map((a) => ({ id: a._id, label: a.customId || a.name }));
	},
	account: async () => {
		const { data } = await AccountService.list(true);
		return data.map((a) => ({ id: a._id, label: a.name }));
	},
	bankAccount: async () => {
		const { data } = await BankAccountService.list();
		return data.map((b) => ({ id: b._id, label: [b.bank, b.accountNumber, b.currency && `(${b.currency})`].filter(Boolean).join(' ') }));
	},
};

/** Typeahead options per reference field, loaded once and only for the fields that need them. */
export const useReferenceOptions = (fieldKeys) => {
	const [optionsByField, setOptionsByField] = useState({});
	const wanted = [...new Set(fieldKeys)].filter((key) => LOADERS[key]).sort().join(',');
	useEffect(() => {
		let active = true;
		wanted
			.split(',')
			.filter((key) => key && !optionsByField[key])
			.forEach((key) => {
				LOADERS[key]()
					.then((options) => active && setOptionsByField((prev) => ({ ...prev, [key]: options })))
					.catch((err) => toast.error(getError(err)));
			});
		return () => {
			active = false;
		};
	}, [wanted]);
	return optionsByField;
};

/** Typeahead over a reference's records with the server's suggestions listed first. */
export function RecordPicker({ id, options, suggestions = [], selected, onChange }) {
	const { t } = useLabels();
	const ordered = useMemo(() => {
		const suggestedIds = new Set(suggestions.map((s) => s.id));
		return [...suggestions.map((s) => ({ ...s, suggested: true })), ...(options || []).filter((o) => !suggestedIds.has(o.id))];
	}, [options, suggestions]);
	return (
		<CustomTypeahead
			id={id}
			variant='entity'
			labelKey={(option) => (option.suggested ? `★ ${option.label}` : option.label)}
			options={ordered}
			selected={selected ? [selected] : []}
			onChange={(chosen) => onChange(chosen[0] ? { id: chosen[0].id, label: chosen[0].label } : null)}
			placeholder={t('import.searchRecord')}
			emptyLabel={t('import.noResults')}
		/>
	);
}

RecordPicker.propTypes = {
	id: PropTypes.string.isRequired,
	options: PropTypes.array,
	suggestions: PropTypes.array,
	selected: PropTypes.object,
	onChange: PropTypes.func.isRequired,
};

/** Input for a missing text, number or enum value (dates use DatePartsInput). */
export function ValueInput({ id, field, value, onChange }) {
	if (field?.type === 'enum') {
		return <Select id={id} value={value ?? ''} onChange={(e) => onChange(e.target.value)} options={Object.keys(field.enum || {})} />;
	}
	return (
		<input
			id={id}
			type='text'
			inputMode={field?.type === 'number' ? 'decimal' : undefined}
			className={`${controlClass} tw:w-full tw:px-2.5`}
			value={value ?? ''}
			onChange={(e) => onChange(e.target.value)}
		/>
	);
}

ValueInput.propTypes = {
	id: PropTypes.string.isRequired,
	field: PropTypes.object,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};

/**
 * One line per distinct file value that matched no record ("Corresponde a",
 * Padrón, Titular...). Picking a record applies to every row with that value.
 */
export function UnresolvedValues({ groups, fieldLabel, optionsByField, picks, onPick }) {
	const { t } = useLabels();
	if (groups.length === 0) return null;
	return (
		<Section title={t('import.unresolved.title')} description={t('import.unresolved.help')}>
			<div className='tw:flex tw:flex-col tw:divide-y tw:divide-border'>
				{groups.map((group) => (
					<div
						key={`${group.field}|${group.key}`}
						className='tw:grid tw:grid-cols-1 tw:items-center tw:gap-2 tw:py-2.5 tw:sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]'
					>
						<div className='tw:min-w-0'>
							<div className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3'>
								{fieldLabel(group.field)}
							</div>
							<div className='tw:truncate tw:text-[13.5px] tw:text-ink-0'>«{group.value}»</div>
							<div className='tw:text-[12px] tw:text-ink-3'>
								{group.rowNumbers.length} {t('import.unresolved.rows')}
							</div>
						</div>
						<RecordPicker
							id={`pick-${group.field}-${group.key}`}
							options={optionsByField[group.field]}
							suggestions={group.suggestions}
							selected={picks[group.field]?.[pickKey(group.value)] || null}
							onChange={(option) => onPick(group.field, group.value, option)}
						/>
					</div>
				))}
			</div>
		</Section>
	);
}

UnresolvedValues.propTypes = {
	groups: PropTypes.array.isRequired,
	fieldLabel: PropTypes.func.isRequired,
	optionsByField: PropTypes.object.isRequired,
	picks: PropTypes.object.isRequired,
	onPick: PropTypes.func.isRequired,
};

const DATE_PARTS = [
	{ key: 'day', labelKey: 'import.dateParts.day', width: 'tw:w-[52px]' },
	{ key: 'month', labelKey: 'import.dateParts.month', width: 'tw:w-[52px]' },
	{ key: 'year', labelKey: 'import.dateParts.year', width: 'tw:w-[72px]' },
];

/** A date as three editable inputs (día / mes / año); parts: { day, month, year }. */
export function DatePartsInput({ id, parts, onChange }) {
	const { t } = useLabels();
	return (
		<div className='tw:flex tw:items-center tw:gap-1'>
			{DATE_PARTS.map((part, i) => (
				<React.Fragment key={part.key}>
					{i > 0 && <span className='tw:text-ink-3'>/</span>}
					<input
						id={`${id}-${part.key}`}
						aria-label={t(part.labelKey)}
						placeholder={t(part.labelKey)}
						inputMode='numeric'
						maxLength={part.key === 'year' ? 4 : 2}
						className={`${controlClass} ${part.width} tw:px-1.5 tw:text-center`}
						value={parts?.[part.key] ?? ''}
						onChange={(e) => onChange({ ...parts, [part.key]: e.target.value.replace(/\D/g, '') })}
					/>
				</React.Fragment>
			))}
		</div>
	);
}

DatePartsInput.propTypes = {
	id: PropTypes.string.isRequired,
	parts: PropTypes.object,
	onChange: PropTypes.func.isRequired,
};
